import {
    DocumentView,
    MediaPreviewStatus,
    ShareBaseDocument,
    PermissionType
} from 'phoenix/domain';
import { 
    MediaService, 
    MediaPreview 
} from 'sharebase/services/mediaService';
import { ProgressService } from 'phoenix/services';
import { ToastService } from 'sharebase/services/toastService';
import { QueryStringFilter } from 'phoenix/filters/queryStringUri';
import { AuthUriFilter } from 'phoenix/filters/authUri';

const template = require('./mediaViewer.template');
require('./mediaViewer.styles')

let userNotifications: { [docId: number]: Function } = { };

export enum VideoControl {
    NoDownload = 'nodownload',
    NoFullScreen = 'nofullscreen',
    NoRemotePlayBack = 'noremoteplayback',
    Default = ''
}

export enum MediaAction {
    Ended = 'ENDED'
}
/**
 * @ngdoc directive
 * @name sbMediaViewer
 * @module sharebase.media
 * @restrict E
 * @description
 * 
 * A directive for viewing video and and audio documents.
 */
export function MediaViewerDirective($timeout: ng.ITimeoutService,$interval: ng.IIntervalService): ng.IDirective {
    return {
        restrict: 'E',
        controller: MediaViewerController,
        controllerAs: 'vm',
        template,
        scope: { },
        bindToController: {
            document: '=',
            autoplay:'<',
            handleMediaError: '&',
            download: '&',
            ended:'&',
            handlePreviewNotification: '&',
            previewNotificationActionText: '@',
            allowDownload: '<'
        },
        link: function(scope: ng.IScope, element, attrs, vm: MediaViewerController) {
            
            /**
             * Gets a single media element from the DOM. If no media element
             * exists, it returns null. The function prefers video elements
             * over audio elements.
             */
            
            const getMedia = (): HTMLMediaElement => {
                // try video first
                let media = element.find('video');
                if (media.length > 0) {
                    return media[0] as HTMLMediaElement;
                }

                // no video elements found, try audio
                media = element.find('audio');
                if (media.length > 0) {
                    return media[0] as HTMLMediaElement;
                }

                return null;
            }

            // Repaint video on orientation changes to make sure
            // the controls are set properly
            const orientationHandler = () => {
                let video = getMedia();
                if (video && video.nodeName.toLowerCase() === 'video') {
                    $timeout(() => {
                        video.style.display = 'none';
                        $timeout(() => video.style.display = 'block', 80)
                    }, 100);
                }
            }



            window.addEventListener('orientationchange', orientationHandler);

            // let vm = scope.vm as MediaViewerController;
            let isReadySub = scope.$watch('vm.isReady', () => {
                let video = getMedia();
                if (video) {
                    video.addEventListener('error', (ev: Event) => {
                        vm.emitError(video.error);
                    });
                }
            });

            let documentSub = scope.$watch('vm.document', (newValue, oldValue) => {
                if (newValue !== oldValue) {
                    vm.init(true);
                }
            });

            scope.$on('$destroy', () => {
                isReadySub();
                documentSub();
                window.removeEventListener('orientationchange', orientationHandler);
                if (vm.previewUpdateSub) {
                    vm.previewUpdateSub();
                }

                // If the component has a preview object, stop it, unless the
                // user wants to be notified of when the preview is ready.
                if (vm.preview) {
                    if (userNotifications[vm.documentId]) {
                        // Set this flag to true so the preview object knows the
                        // user has navigated away and it should actually display
                        // the notification. If the user doesn't navigate away,
                        // the notification is not necessary
                        vm.shouldNotify = true;
                    } else {
                        vm.preview.stop();
                    }
                }
            });
        }
    };
}

MediaViewerDirective.$inject = [
    '$timeout',
    '$interval'
];
export interface MediaViewerErrorCallback {
    (err: { error: MediaError, video: boolean }): void;
}

export interface MediaViewerPreviewNotificationActionCallback {
    (data: { document: ShareBaseDocument }): void;
}

export interface MediaDownloadCallback {
    (data: { document: ShareBaseDocument }): void;
}

export class MediaViewerController {
    public static readonly $inject: string[] = [
        '$q',
        '$sce',
        '$attrs',
        'pxProgressService',
        'pxAuthUriFilter',
        'pxQueryStringFilter',
        'sbMediaService',
        'sbToastService',
        'gettextCatalog'
    ];

    private url:string = '';

    // bindings
    public readonly handleMediaError: MediaViewerErrorCallback;
    public readonly download: MediaDownloadCallback;
    public readonly document: DocumentView;
    public readonly handlePreviewNotification: MediaViewerPreviewNotificationActionCallback;
    public readonly previewNotificationActionText: string;
    public readonly allowDownload: boolean;

    // Flag that indicates the component should actually notify the user.
    // This will only be set to true when the user navigates away and the component gets destroyed.
    // If the user remains on the page, then a notification is not necessary
    public shouldNotify: boolean = false;

    // Manages state of preview generation
    private previewStatus: MediaPreviewStatus;

    // Manages state of preview generation
    public comment: string = '';

    public preview: MediaPreview;
    public previewUpdateSub: () => void;

    public get videoControls(): string {
        return this.allowDownload ? VideoControl.Default : VideoControl.NoDownload;
    }

    public get percentComplete(): number {
        return this.previewStatus.PercentComplete;
    }

    /**
     * Check if a preview is ready to be viewed.
     * 
     * @returns True if the preview is ready or false it is still
     * being generated.
     */
    public get isReady(): boolean {
        return this.previewStatus.PreviewStatus === 'Available';
    }

    /**
     * Check if the video preview is in progress
     * 
     * @returns True if the preview is still being generated.
     */
    public get inProgress(): boolean {
        return this.previewStatus.PreviewStatus === 'InProgress';
    }

    /**
     * Check if the video preview is unavailabe. This will typically
     * happen if there was some server error when generating the preview.
     * 
     * @returns True if the preview cannot be viewed in a browser.
     */
    public get unavailable(): boolean {
        return this.previewStatus.PreviewStatus === 'Unavailable';
    }

    /**
     * Get the name of the document being viewed.
     */
    public get documentName(): string {
        return this.document.Embedded.Document.DocumentName;
    }

    /**
     * Get the proper src url that can be used for either video or audio elements.
     * 
     * @returns The url that can be assigned to a media element's src attribute.
     */
    public get src(): string {
        return this.url && this.$sce.trustAsResourceUrl(this.url);
    }

    /**
     * Check if the document is a video file.
     * 
     * @returns True if the document represents a video file.
     */
    public get isVideo() {
        return this.mediaService.isVideo(this.document.Embedded.Document);
    }

    /**
     * Check if the document is an audio file.
     * 
     * @returns True if the document represents an audio file.
     */
    public get isAudio() {
        return this.mediaService.isAudio(this.document.Embedded.Document);
    }

    /**
     * Check if the directive provided a download handler.
     * 
     * @returns True if the consuming directive provides a download handler.
     */
    public get showDownload() {
        return !!this.$attrs.download && this.allowDownload;
    }

    /**
     * Title for media elements
     */
    public get mediaTitle(): string {
        return this.document.Embedded.Document.DocumentName;
    }

    public get documentId(): number { 
        return this.document.Embedded.Document.DocumentId;
    }

    constructor(
        private readonly $q:ng.IQService,
        private readonly $sce:ng.ISCEService,
        private readonly $attrs,
        private readonly pxProgressService:ProgressService,
        private readonly pxAuthUriFilter:AuthUriFilter,
        private readonly pxQueryStringFilter:QueryStringFilter,
        private readonly mediaService:MediaService,
        private readonly sbToastService:ToastService,
        private readonly gettextCatalog:ng.gettext.gettextCatalog
    ) {
        this.initPreviewStatus();
        this.init();
    }

    /**
     * Continuously calls a service to get updates on the preview
     * generation. This method will continue as long as the preview
     * status is "InProgress".
     * 
     * @param timeDelay An optional delay to start pinging the service.
     */
    private getPreviewStatus(timeDelay:number = 0) {
        let d = this.$q.defer<MediaPreviewStatus>();
        
        this.preview = this.mediaService.getPreviewStatus(this.document);
        this.previewUpdateSub = this.preview.subscribe((doc, status) => {
            this.previewStatus = status;
        });

        let unsubscription = this.preview.subscribe((doc, status) => {
            switch(status.PreviewStatus){
                case 'Available':
                    d.resolve(status);
                    cleanUpReferences();
                    break;
                case 'Unavailable':
                    d.reject(new Error('This video preview is unavailable'));
                    cleanUpReferences();
                    break;
            }
        });

        return d.promise;

        function cleanUpReferences(){
            if(unsubscription){
                unsubscription();
            }
            d = unsubscription = null;
        }
    }

    /**
     * Sets initial values on the controller, for the preview generation state.
     * 
     * @param immediate Optional flag to indiate if preview generation state should
     * be initialized immediately or if it should wait until the data has been loaded.
     */
    public init(immediate?: boolean) {
        let p:ng.IPromise<string>;
        if (immediate) {
            this.initPreviewStatus();
        }

        if (this.isAudio) {
            this.initPreviewStatus();
            p = this.$q.resolve(this.document.Embedded.Document.Links.ContentUri_v2);
        } else {
            let previewPromise:ng.IPromise<MediaPreviewStatus>;

            if (this.document.PreviewStatus === 'Available') {
                this.previewStatus = {
                    PercentComplete: 100,
                    PreviewStatus: 'Available',
                    Url: this.document.PageTemplate
                };

                previewPromise = this.$q.resolve(this.previewStatus);
            } else {
                this.initPreviewStatus();
                previewPromise = this.getPreviewStatus(500);
            }
            
            p = previewPromise.then(status => status.Url);
        }

        this.pxProgressService.enqueue(p);
        return p.then(url => {
            let urlParams = {
                ctx: this.document.PageTemplate.split('ctx=')[1]
            };
            this.url = this.pxQueryStringFilter(this.pxAuthUriFilter(url), urlParams);
        })
        .catch((e:Error) => {
            // we can eat the error if it's due to the preview being unavailable
            // since there is view logic to handle it with the property on this controller
            if(this.previewStatus.PreviewStatus !== 'Unavailable'){
                throw e;
            }
        });
    }

    /**
     * Handles errors from media elements by calling the consuming directives
     * mediaError handler.
     * 
     * @param error The error being emitted.
     */
    public emitError(error: MediaError): void {
        this.handleMediaError({ error, video: this.isVideo });
    }

    /**
     * Tell consuming directives the user wishes to download the document.
     */
    public emitDownload(): void {
        this.download({ document: this.document.Embedded.Document });
    }

    /**
     * Sets up the component to notify the user when a preview is ready.
     */
    public notifyUser() {
        // If we already have a notification registered for this document, don't add another one.
        if (userNotifications[this.documentId]) {
            return;
        }

        let userNotificationSub = this.preview.subscribe((doc, status) => {
            if (status.PreviewStatus === 'Available' && this.shouldNotify) {
                let message = this.gettextCatalog.getString('{{documentName}} preview is ready', { documentName: this.documentName });
                let actionText = this.previewNotificationActionText || this.gettextCatalog.getString('OPEN');

                // If the consumer provided a preview action, show it in the notification,
                // otherwise, just show a simple message.
                if (this.handlePreviewNotification) {
                    this.sbToastService.showSimpleAction(message, actionText, () => this.handlePreviewNotification({ document: this.document.Embedded.Document }), 10000, 'open_in_browser');
                } else {
                    this.sbToastService.showSimple(message);
                }

                this.stopNotifyUser();
            }
        });

        userNotifications[this.documentId] = userNotificationSub;
        this.sbToastService.showSimple(this.gettextCatalog.getString('You will be notified when {{documentName}} is ready to watch.', {documentName: this.documentName}));

    }   

    public isUserNotifyOn(){
        return userNotifications[this.documentId] ? true : false;
    }

    public stopNotifyUser(){
        // After notification callback has run, clean it up as
        // well as the subscription on the preview object
        userNotifications[this.documentId]();
        delete userNotifications[this.documentId];
    }

    /**
     * Initialize preview generation state with default values.
     */
    private initPreviewStatus(): void {
        this.previewStatus = {
            PercentComplete: 0,
            PreviewStatus: 'Init',
            Url: ''
        };
    }
}


// WEBPACK FOOTER //
// ./src/sharebase/components/mediaViewer/mediaViewer.ts